import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';

export interface TooltipProps {
  /** Tooltip body. Keep it short — cost breakdowns belong in a popover. */
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

/** Gap between the trigger and the bubble, in px. */
const OFFSET = 8;
/** Keep the bubble this far from the viewport edge. */
const MARGIN = 8;

interface Position {
  top: number;
  left: number;
}

/**
 * Portalled tooltip.
 *
 * It renders into `document.body` and positions itself with `fixed`
 * coordinates measured from the trigger. That is not an implementation detail
 * you can swap freely — it is the fix for two bugs an absolutely-positioned
 * tooltip cannot avoid:
 *
 *   • Clipping. The resource strip and every scrollable panel are overflow
 *     containers; a tooltip positioned inside one is cut off at its edge.
 *   • Stacking. `z-index` only orders siblings within a stacking context, so a
 *     tooltip living inside card #1 paints *behind* card #2 no matter how high
 *     its z-index — which is exactly the "hover sits behind other boxes" bug.
 *
 * Opens on hover *and* focus so keyboard users get the same information, and
 * is wired via `aria-describedby` rather than `title` so the content can be rich.
 */
export function Tooltip({ content, children, side = 'top', className }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  const place = useCallback(() => {
    const trigger = triggerRef.current?.getBoundingClientRect();
    const bubble = bubbleRef.current?.getBoundingClientRect();
    if (!trigger || !bubble) return;

    let top: number;
    let left: number;
    switch (side) {
      case 'bottom':
        top = trigger.bottom + OFFSET;
        left = trigger.left + trigger.width / 2 - bubble.width / 2;
        break;
      case 'left':
        top = trigger.top + trigger.height / 2 - bubble.height / 2;
        left = trigger.left - bubble.width - OFFSET;
        break;
      case 'right':
        top = trigger.top + trigger.height / 2 - bubble.height / 2;
        left = trigger.right + OFFSET;
        break;
      default:
        top = trigger.top - bubble.height - OFFSET;
        left = trigger.left + trigger.width / 2 - bubble.width / 2;
    }

    // Clamp into the viewport so a tooltip at the edge of the screen stays whole.
    const maxLeft = window.innerWidth - bubble.width - MARGIN;
    const maxTop = window.innerHeight - bubble.height - MARGIN;
    setPosition({
      top: Math.max(MARGIN, Math.min(top, Math.max(MARGIN, maxTop))),
      left: Math.max(MARGIN, Math.min(left, Math.max(MARGIN, maxLeft))),
    });
  }, [side]);

  // Measure after the bubble mounts, before the browser paints it.
  useLayoutEffect(() => {
    if (open) place();
    else setPosition(null);
  }, [open, place, content]);

  // Scrolling or resizing moves the trigger; follow it rather than detach.
  useEffect(() => {
    if (!open) return;
    const onChange = () => place();
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
    };
  }, [open, place]);

  const bubble = open ? (
    <span
      ref={bubbleRef}
      role="tooltip"
      id={id}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        // Invisible for the one frame it takes to measure, so it never flashes
        // in the top-left corner before landing.
        visibility: position ? 'visible' : 'hidden',
      }}
      className={cn(
        // `sg-root` so the bubble keeps the design system's typography even
        // when it is portalled out of the shell that normally provides it.
        'sg-root pointer-events-none fixed z-tooltip w-max max-w-[16rem]',
        'rounded-control border border-edge-strong bg-surface-overlay px-2.5 py-1.5',
        'text-xs leading-snug text-content-secondary shadow-panel-lg',
        className,
      )}
    >
      {content}
    </span>
  ) : null;

  return (
    <span
      ref={triggerRef}
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span aria-describedby={open ? id : undefined} className="inline-flex">
        {children}
      </span>
      {bubble ? createPortal(bubble, portalTarget(triggerRef.current)) : null}
    </span>
  );
}

/**
 * Where the bubble mounts.
 *
 * The nearest `.sg-root` when there is one, so the themed CSS variables and
 * the scoped reset still apply; `document.body` otherwise. Either way it is
 * outside every scrolling panel and every card's stacking context, which is
 * the entire point. `position: fixed` escapes the shell's `overflow-x-clip`
 * because that ancestor sets no transform or filter.
 */
function portalTarget(trigger: HTMLElement | null): HTMLElement {
  return trigger?.closest<HTMLElement>('.sg-root') ?? document.body;
}
